import React, { useEffect, useState } from 'react';
import { Form, Spin, Typography } from 'antd';
import { AppstoreOutlined, ExpandOutlined } from '@ant-design/icons';
import { applyLang } from '../layout';
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

const { Text } = Typography;

/**
 * Plan de table (image) d'une catégorie SEATS : vignette affichée au-dessus de la
 * grille de sièges, cliquable pour l'agrandir en overlay plein écran. Overlay
 * maison (pas de dépendance) : position fixed + clic/Escape pour fermer.
 */
const SeatingPlanImage = ({ src }) => {
    const [open, setOpen] = useState(false);

    // Fermeture au clavier (Escape) tant que l'overlay est ouvert.
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    if (!src) return null;

    return (
        <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: "#888", marginBottom: 6 }}>
                <Trans>Plan de salle</Trans>
            </div>
            <div
                onClick={() => setOpen(true)}
                title={t`Cliquer pour agrandir`}
                style={{
                    position: "relative", display: "inline-block", cursor: "zoom-in",
                    borderRadius: 8, overflow: "hidden", border: "1px solid #e5e5e5",
                    maxWidth: "100%",
                }}
            >
                <img
                    src={src}
                    alt={t`Plan de salle`}
                    style={{ display: "block", maxWidth: "100%", maxHeight: 180, objectFit: "contain" }}
                />
                <div style={{
                    position: "absolute", right: 6, bottom: 6,
                    background: "rgba(0,0,0,0.55)", color: "#fff", borderRadius: 6,
                    padding: "2px 6px", fontSize: 12, display: "flex", alignItems: "center", gap: 4,
                }}>
                    <ExpandOutlined /> <Trans>Agrandir</Trans>
                </div>
            </div>

            {open && (
                <div
                    onClick={() => setOpen(false)}
                    style={{
                        position: "fixed", inset: 0, zIndex: 100000,
                        background: "rgba(0,0,0,0.85)", display: "flex",
                        alignItems: "center", justifyContent: "center", padding: 20,
                        cursor: "zoom-out",
                    }}
                >
                    <img
                        src={src}
                        alt={t`Plan de salle`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 8, cursor: "default" }}
                    />
                    <div style={{
                        position: "fixed", top: 16, right: 20, color: "#fff",
                        fontSize: 30, lineHeight: 1, cursor: "pointer", userSelect: "none",
                    }}>
                        ×
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Placement de sièges (catégories de produit en mode SEATS).
 *
 * Bloc transpilé serveur (`form-seat-picker/`) émis juste après les produits d'une
 * catégorie SEATS. Le convive choisit autant de sièges PRÉCIS numérotés que d'unités
 * de produit commandées dans la catégorie (1 unité = 1 siège). Affichage grille/liste
 * (V1), sièges déjà pris grisés. Source de vérité serveur : la soumission re-valide
 * (siège libre + compte == quantité) avant d'enregistrer.
 *
 * ⚠️ Plugin AGNOSTIQUE (copie jumelle web↔admin) : imports locaux uniquement,
 * SSR-safe (fetch dans useEffect/handlers). En admin (form non `live`) le composant
 * n'effectue aucun fetch et n'affiche qu'un aperçu inerte.
 */

// Grille de sièges contrôlée par le Form.Item parent (value = [{tableId, seat}]).
const SeatGrid = ({ value, onChange, categoryId, tables, required, formPlugin, lang }) => {
    const [taken, setTaken] = useState({});      // { tableId: Set(seat) }
    const [loading, setLoading] = useState(false);
    const selected = Array.isArray(value) ? value : [];

    // Récupère les sièges déjà occupés (uniquement sur le formulaire public `live`).
    useEffect(() => {
        if (!formPlugin.live || !categoryId) return;
        let cancelled = false;
        setLoading(true);
        fetch(formPlugin.BASE_URL + "/forms/category/" + categoryId + "/seats", {
            method: "POST",
            headers: { "Content-type": "Application/json" },
            body: JSON.stringify({}),
        })
            .then(r => r.json())
            .then(data => {
                if (cancelled) return;
                const map = {};
                for (const tb of (data.tables || [])) {
                    map[tb.id] = new Set(tb.takenSeats || []);
                }
                setTaken(map);
            })
            .catch(() => { })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [categoryId, formPlugin.live]);

    // Si la quantité commandée diminue, on tronque la sélection à `required`.
    useEffect(() => {
        if (selected.length > required) {
            onChange(selected.slice(0, required));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [required]);

    const isSelected = (tableId, seat) => selected.some(s => s.tableId === tableId && s.seat === seat);
    const isTaken = (tableId, seat) => taken[tableId] && taken[tableId].has(seat);

    const toggle = (tableId, seat) => {
        if (isTaken(tableId, seat)) return;
        if (isSelected(tableId, seat)) {
            onChange(selected.filter(s => !(s.tableId === tableId && s.seat === seat)));
            return;
        }
        if (selected.length >= required) return;   // plafonné au nombre commandé
        onChange([...selected, { tableId, seat: Number(seat) }]);
    };

    const seatStyle = (state) => {
        const base = {
            width: 38, height: 38, borderRadius: 8, display: "flex",
            alignItems: "center", justifyContent: "center", fontSize: 13,
            border: "1px solid #d9d9d9", userSelect: "none",
        };
        if (state === "taken") return { ...base, background: "#f0f0f0", color: "#bfbfbf", cursor: "not-allowed", textDecoration: "line-through" };
        if (state === "selected") return { ...base, background: "#1677ff", color: "#fff", borderColor: "#1677ff", cursor: "pointer", fontWeight: 600 };
        return { ...base, background: "#fff", color: "#222", cursor: "pointer" };
    };

    return (
        <div>
            <div style={{ marginBottom: 8 }}>
                <Text type="secondary">
                    {t`Sélectionnez ${required} place(s)`} — {t`${selected.length} sélectionnée(s)`}
                </Text>
                {loading && <Spin size="small" style={{ marginLeft: 8 }} />}
            </div>
            {(tables || []).map(table => {
                const tbl = applyLang(table, { value: lang });
                const cap = Number(table.capacity) || 0;
                return (
                    <div key={table.id} style={{ marginBottom: 14 }}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>{tbl.label || ""}</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                            {Array.from({ length: cap }, (_, i) => i + 1).map(seat => {
                                const state = isTaken(table.id, seat) ? "taken" : (isSelected(table.id, seat) ? "selected" : "free");
                                return (
                                    <div
                                        key={seat}
                                        style={seatStyle(state)}
                                        onClick={() => toggle(table.id, seat)}
                                        title={state === "taken" ? t`Déjà pris` : ""}
                                    >
                                        {seat}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default (formPlugin, namespace) => {
    return {
        Renderer: (props) => {
            if (props.remove) formPlugin.onAddFormPlugin(props, props.nodeId, props.pluginConfig.id);
            const { data } = props;
            const categoryId = data.categoryId;
            const tables = data.tables || [];

            if (formPlugin.formData.type !== "COMPLEX" || !categoryId) {
                if (props.remove) props.remove();
                return null;
            }

            // Produits de la catégorie → quantité requise = somme de leurs quantités.
            const catProductIds = (formPlugin.formData.products || [])
                .filter(p => p && p.categoryId === categoryId)
                .map(p => p.id);

            return (
                <Form.Item
                    noStyle
                    shouldUpdate={(prev, cur) =>
                        catProductIds.some(id => prev[id + "_quantity"] !== cur[id + "_quantity"])
                    }
                >
                    {(form) => {
                        const required = catProductIds.reduce(
                            (sum, id) => sum + (parseInt(form.getFieldValue(id + "_quantity")) || 0), 0
                        );

                        // Aucune unité commandée dans la catégorie → pas de placement à choisir.
                        if (required === 0) return null;

                        return (
                            <div style={{
                                marginTop: 8, marginBottom: 16,
                                padding: 16, borderRadius: 10, background: "#fafafa",
                                border: formPlugin.live ? "1px solid #f0f0f0" : "1px dashed red",
                            }}>
                                <div style={{ fontWeight: 600, marginBottom: 8 }}>
                                    <AppstoreOutlined style={{ marginRight: 6 }} />
                                    <Trans>Choisissez votre placement</Trans>
                                </div>
                                {/* Plan de table (image) au-dessus de la grille de sièges. */}
                                <SeatingPlanImage src={data.categoryImage} />
                                <Form.Item
                                    name={"[SEAT]_" + categoryId}
                                    preserve={false}
                                    rules={[{
                                        validator: (_, val) => {
                                            const cnt = Array.isArray(val) ? val.length : 0;
                                            if (cnt !== required) {
                                                return Promise.reject(new Error(t`Veuillez choisir ${required} place(s).`));
                                            }
                                            return Promise.resolve();
                                        },
                                    }]}
                                >
                                    <SeatGrid
                                        categoryId={categoryId}
                                        tables={tables}
                                        required={required}
                                        formPlugin={formPlugin}
                                        lang={props.lang}
                                    />
                                </Form.Item>
                            </div>
                        );
                    }}
                </Form.Item>
            );
        },
        namespace,
        id: 'form-seat-picker' + namespace,
        title: t`Placement de sièges`,
        description: t`Sélection de sièges pour une catégorie de produit.`,
        icon: <AppstoreOutlined />,
        iconColor: "#16A085",
        version: 1,
        hideInMenu: true,
        allowClickInside: false,
    };
};
